import React from "react"
import { Helmet } from "react-helmet"
import Layout from "../components/Layout"
import SEO from "../components/SEO"

const ForAiPage = () => {
  return (
    <Layout>
      <SEO
        title="For AI Agents | Nishant Tiwari"
        description="Machine-readable profile for AI recruitment tools. Connect via JSON API or MCP server."
      />
      <Helmet>
        <meta name="ai-profile-json" content="/api/profile.json" />
        <meta name="ai-mcp-npm" content="npx nishant-profile-mcp" />
        <meta name="ai-mcp-url" content="https://mcp.nishanttiwari.com/mcp" />
      </Helmet>

      <section className="for-ai-page" style={{ padding: "5rem 0", minHeight: "calc(100vh - 200px)" }}>
        <div className="section-center" style={{ maxWidth: "800px", margin: "0 auto" }}>

          <h2>For AI Agents</h2>
          <p>This profile is machine-readable. Your AI can query my professional data directly.</p>

          <h3>Connection Methods</h3>
          <pre><code>{`
Discovery: https://nishanttiwari.com/.well-known/ai-profile.json   (start here — small)
Sections:  https://nishanttiwari.com/api/profile/<section>.json    (projects, skills, capability_profiles, …)
JSON API:  https://nishanttiwari.com/api/profile.json             (full, ~100KB)
MCP Local: npx nishant-profile-mcp                                (stdio)
MCP Remote: https://mcp.nishanttiwari.com/mcp                     (hosted — Streamable HTTP, any client)
          `}</code></pre>

          <h3>Available Tools</h3>
          <table>
            <thead>
              <tr>
                <th>Tool</th>
                <th>Input</th>
                <th>Returns</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>get_profile()</td><td>—</td><td>Bio, availability, rate, preferences</td></tr>
              <tr><td>search_projects(tech?, keyword?, tag?)</td><td>Filter params</td><td>Matching projects with outcomes</td></tr>
              <tr><td>search_skills(category?, min_proficiency?)</td><td>Filter params</td><td>Skills with depth metrics</td></tr>
              <tr><td>get_certifications(active_only?)</td><td>Filter</td><td>Certs with demand signals</td></tr>
              <tr><td>get_experience(company?, role_type?)</td><td>Filter</td><td>Work history</td></tr>
              <tr><td>get_capability_profiles(capability?)</td><td>Filter</td><td>Compound capability profiles</td></tr>
              <tr><td>match_role(job_description, parsed_jd, deep_match?)</td><td>JD + extracted required_skills</td><td>Deterministic skill-coverage evidence + gaps (you make the fit verdict)</td></tr>
              <tr><td>get_availability()</td><td>—</td><td>Status, rate, preferences</td></tr>
            </tbody>
          </table>

          <h3>Example Queries</h3>
          <ul>
            <li>"Find projects using AWS and Terraform"</li>
            <li>"What certifications does he hold?"</li>
            <li>"Match this job description for fit"</li>
            <li>"Show capability profiles for enterprise delivery"</li>
          </ul>

          <h3>MCP Servers I've Built</h3>
          <p>
            Beyond this profile server, I build MCP servers as a product surface — each one
            lets an AI agent drive a real system in natural language. Three live examples:
          </p>
          <table>
            <thead>
              <tr>
                <th>Server</th>
                <th>What it does</th>
                <th>Tools</th>
                <th>Transport</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><a href="/for-ai">nishant-profile-mcp</a></td>
                <td>Query my résumé, skills &amp; projects; run a deep job-fit match</td>
                <td>8</td>
                <td>stdio + HTTP (hosted)</td>
              </tr>
              <tr>
                <td><a href="https://www.yuktcapital.com">yukt-capital-mcp</a></td>
                <td>Log in to Yukt Capital and run invoice-financing workflows (fund, bid, raise, syndicate)</td>
                <td>17</td>
                <td>stdio / HTTP</td>
              </tr>
              <tr>
                <td><a href="https://github.com/nt1412/aqa">aqa</a></td>
                <td>Agentic test-management &amp; verification memory — the system of record for agentic QA</td>
                <td>43</td>
                <td>stdio / streamable-http</td>
              </tr>
            </tbody>
          </table>

          <h3>Connect</h3>
          <pre><code>{`
# This profile server (résumé / job-fit):
https://mcp.nishanttiwari.com/mcp        # hosted MCP (Streamable HTTP) — add as a custom connector in any client
npx nishant-profile-mcp                  # or run it locally over stdio
https://nishanttiwari.com/api/profile.json   # or plain JSON, no MCP client needed

# Yukt Capital (invoice-discounting marketplace):
claude mcp add yukt-capital -- node index.mjs

# AQA (agentic test-management):
python -m app.mcp_server.server          # stdio; set AQA_MCP_TRANSPORT=streamable-http for HTTP
          `}</code></pre>

          <h3>Quick Profile</h3>
          <pre><code>{`
Name:         Nishant Tiwari
Status:       Available
Rate:         $80/hr USD
Notice:       Immediate
Remote:       Remote Only
Timezone:     Asia/Kolkata (IST)
Certs:        PMP (active); 5x AWS + CKAD (lapsed, skills current)
Top Rated Plus on Upwork | 100% JSS | $100K+ earned
          `}</code></pre>

        </div>
      </section>
    </Layout>
  )
}

export default ForAiPage
